import { twMerge } from 'tailwind-merge';
import Table from '../../data-display/Table';
import ListCardCell from '../ListCardCell';
import ListActionCell from '../ListActionCell';
import ListTableState from '../ListTableState';
import { useLanguage } from '@/chassis/ui/i18n/useLanguage';
import type { Language } from '@/chassis/ui/i18n/i18n-helper';

type ListTableVariant = 'card' | 'flat';

interface ListTableProps extends React.ComponentPropsWithoutRef<typeof Table> {
    variant?: ListTableVariant;
}

// Rodada 2 (28/10): as linhas "soltas" com sombra viraram uma tabela limpa, com
// filete Neutro 200 entre as linhas e hover em Roxo 50.
const HOVER = '[&>tbody>tr:hover>td]:bg-roxo-50 dark:[&>tbody>tr:hover>td]:bg-darkmode-500/70';
const baseClasses: Record<ListTableVariant, string> = {
    card: `border-separate border-spacing-0 ${HOVER}`,
    flat: `min-w-full bg-white dark:bg-darkmode-600 ${HOVER}`,
};

function ListTable({ className, variant = 'card', ...props }: ListTableProps) {
    return <Table className={twMerge([baseClasses[variant], className])} {...props} />;
}

ListTable.Thead = Table.Thead;
ListTable.Tbody = Table.Tbody;
ListTable.Tr = Table.Tr;

type I18nLabel = Record<Language, string>;
// Aceita tanto I18nLabel object quanto plain string (pt-only). Muitos módulos têm
// dicts legados `Record<string, string>` (só pt). Resolver no render é mais barato
// que migrar todos os dicts de uma vez.
type I18nLike = I18nLabel | string;

type HeaderCellProps = React.ComponentPropsWithoutRef<typeof Table.Th> & {
    i18nLabel?: I18nLike;
};
ListTable.HeaderCell = function HeaderCell({ className, i18nLabel, children, ...props }: HeaderCellProps) {
    const lang = useLanguage();
    const resolved = typeof i18nLabel === 'string' ? i18nLabel : i18nLabel?.[lang];
    return (
        <Table.Th className={twMerge(['whitespace-nowrap', className])} {...props}>
            {resolved ?? children}
        </Table.Th>
    );
};

type CellProps = React.ComponentPropsWithoutRef<typeof ListCardCell>;
ListTable.Cell = function Cell({ className, withDarkBackground = true, ...props }: CellProps) {
    return <ListCardCell withDarkBackground={withDarkBackground} className={className} {...props} />;
};

type ActionCellProps = React.ComponentPropsWithoutRef<typeof ListActionCell>;
ListTable.ActionCell = function ActionCell({ alignment = 'center', withDivider = true, ...props }: ActionCellProps) {
    return <ListActionCell alignment={alignment} withDivider={withDivider} {...props} />;
};

type StateProps = React.ComponentPropsWithoutRef<typeof ListTableState>;
ListTable.State = function State(props: StateProps) {
    return <ListTableState {...props} />;
};

export default ListTable;